import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import AppButton from '../../../components/designe/app-button/AppButton';
import AppDialog from '../../../components/reusable/app-dialog/AppDialog';
import AppSelectWrapper from '../../../components/designe/app-select-wrapper/AppSelectWrapper';
import {
  ISupportRequest,
  SupportRequestStatus,
} from '../../../types/interfaces/support-request';
import { getSupportRequestStatusOptions } from './supportStatus';

type Props = {
  open: boolean;
  request: ISupportRequest | null;
  canUpdateStatus: boolean;
  isSaving: boolean;
  classes: Record<string, string>;
  onClose: () => void;
  onSaveStatus: (status: SupportRequestStatus) => void;
};

function formatDate(dateIso: string) {
  if (!dateIso) {
    return '—';
  }

  const date = new Date(dateIso);
  if (Number.isNaN(date.valueOf())) {
    return dateIso;
  }

  return date.toLocaleString();
}

export function SupportRequestDetailsModal(props: Props) {
  const { t } = useTranslation();
  const {
    open,
    request,
    canUpdateStatus,
    isSaving,
    onClose,
    onSaveStatus,
    classes,
  } = props;

  const statusOptions = useMemo(() => getSupportRequestStatusOptions(t), [t]);
  const [status, setStatus] = useState<SupportRequestStatus | null>(null);

  useEffect(() => {
    setStatus(request?.status ?? null);
  }, [request]);

  const selectedStatusOption =
    statusOptions.find((option) => option.id === status) ?? null;
  const statusChanged = !!request && !!status && status !== request.status;

  if (!request) {
    return null;
  }

  const userName =
    request.user?.firstname || request.user?.lastname
      ? `${request.user?.firstname ?? ''} ${request.user?.lastname ?? ''}`.trim()
      : '—';

  function parseNextStatus(value: unknown): SupportRequestStatus {
    if (typeof value === 'string') {
      return value as SupportRequestStatus;
    }

    if (value && typeof value === 'object' && 'id' in value) {
      return (value as { id: SupportRequestStatus }).id;
    }

    return request.status;
  }

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={t('adminDashboard.support.modal.title')}>
      <Box className={classes.modalBody}>
        <Box className={classes.modalField}>
          <Typography
            component="div"
            variant="text-sm"
            className={classes.modalFieldLabel}>
            {t('common.title')}
          </Typography>
          <Typography
            component="div"
            variant="text-md-sb"
            className={classes.modalFieldValue}>
            {request.title}
          </Typography>
        </Box>
        <Box className={classes.modalField}>
          <Typography
            component="div"
            variant="text-sm"
            className={classes.modalFieldLabel}>
            {t('common.user')}
          </Typography>
          <Typography
            component="div"
            variant="text-md"
            className={classes.modalFieldValue}>
            {userName}
          </Typography>
        </Box>
        <Box className={classes.modalField}>
          <Typography
            component="div"
            variant="text-sm"
            className={classes.modalFieldLabel}>
            {t('adminDashboard.support.fields.senderName')}
          </Typography>
          <Typography
            component="div"
            variant="text-md"
            className={classes.modalFieldValue}>
            {request.name}
          </Typography>
        </Box>
        <Box className={classes.modalField}>
          <Typography
            component="div"
            variant="text-sm"
            className={classes.modalFieldLabel}>
            {t('adminDashboard.support.fields.email')}
          </Typography>
          <Typography
            component="div"
            variant="text-md"
            className={classes.modalFieldValue}>
            {request.email}
          </Typography>
        </Box>
        <Box className={classes.modalField}>
          <Typography
            component="div"
            variant="text-sm"
            className={classes.modalFieldLabel}>
            {t('adminDashboard.support.fields.createdAt')}
          </Typography>
          <Typography
            component="div"
            variant="text-md"
            className={classes.modalFieldValue}>
            {formatDate(request.createdAt)}
          </Typography>
        </Box>
        <Box className={classes.modalField}>
          <Typography
            component="div"
            variant="text-sm"
            className={classes.modalFieldLabel}>
            {t('adminDashboard.support.fields.updatedAt')}
          </Typography>
          <Typography
            component="div"
            variant="text-md"
            className={classes.modalFieldValue}>
            {formatDate(request.updatedAt)}
          </Typography>
        </Box>
        <Box className={classes.modalField}>
          <Typography
            component="div"
            variant="text-sm"
            className={classes.modalFieldLabel}>
            {t('adminDashboard.support.fields.message')}
          </Typography>
          <Box className={classes.messageBox}>
            <Typography
              component="div"
              variant="text-md"
              className={classes.modalFieldValue}>
              {request.message}
            </Typography>
          </Box>
        </Box>
        <AppSelectWrapper
          className={classes.statusFilter}
          label={t('moderatorDashboard.status')}
          defaultValue={selectedStatusOption}
          options={statusOptions}
          onChange={(event) => setStatus(parseNextStatus(event?.target?.value))}
          disableClear
          disableSorting
          disabled={!canUpdateStatus}
        />
        {canUpdateStatus && (
          <Box className={classes.modalActions}>
            <AppButton
              onClick={() => status && onSaveStatus(status)}
              loader={isSaving}
              disabled={!statusChanged}>
              {t('common.save')}
            </AppButton>
          </Box>
        )}
      </Box>
    </AppDialog>
  );
}
